import React, { useState, useEffect } from 'react'
import {
  Drawer,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import ChatIcon from '@mui/icons-material/Chat'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import { useDispatch } from 'react-redux'
import { getAIChatResponse } from '../../store/layout/actionCreator'
import { ziptieColors } from '../../config/themeConfig'

const ChatWidget = () => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          sender: 'ai',
          text: 'Disclaimer: AI Chat Assistant is a work in progress so some data may be incomplete or invalid. Please contact us in Slack (#r-and-d-pirates-support) with any questions or feedback!',
        },
      ])
    }
  }, [open, messages.length])

  const handleSend = async () => {
    const trimmed = input.trim()
    if (!trimmed) return
    const updated = [...messages, { sender: 'user', text: trimmed }]
    setMessages(updated)
    setInput('')
    setLoading(true)

    const apiMessages = updated.map((m) => ({
      role: m.sender === 'user' ? 'user' : 'assistant',
      content: m.text,
    }))

    try {
      const aiContent = await dispatch(getAIChatResponse(apiMessages))
      setMessages((prev) => [...prev, { sender: 'ai', text: aiContent }])
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { sender: 'ai', text: `Error: ${e.message}` },
      ])
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {!open && (
        <Tooltip title="Chat with R&D AI Agent" placement="left">
          <IconButton
            sx={{
              position: 'fixed',
              bottom: 12,
              right: 4,
              zIndex: 9999,
              bgcolor: 'background.paper',
              boxShadow: 3,
              width: 40,
              height: 40,
              borderRadius: '50%',
            }}
            onClick={() => setOpen(true)}
            aria-label="Chat with R&D AI Agent"
          >
            <ChatIcon
              sx={{
                fontSize: 24,
                color:
                  process.env.REACT_APP_ENV === 'stg'
                    ? ziptieColors.development.main
                    : ziptieColors.primary.main,
              }}
            />
          </IconButton>
        </Tooltip>
      )}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: messages.length > 0 ? 750 : 550,
              height: messages.length > 0 ? '80vh' : '25vh',
              bottom: 0,
              top: 'auto',
              right: 0,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="h6">R&D Bot - AI Chat Assistant</Typography>
            <Tooltip title="Minimize chat" placement="top">
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                aria-label="Minimize chat"
              >
                <CloseFullscreenIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', mb: 1 }}>
            {messages.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  mb: 1,
                  textAlign: msg.sender === 'user' ? 'right' : 'left',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    display: 'inline-block',
                    bgcolor:
                      msg.sender === 'user' ? 'primary.light' : 'grey.200',
                    color:
                      msg.sender === 'user'
                        ? 'primary.contrastText'
                        : 'text.primary',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    maxWidth: '90%',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {msg.text}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={5}
              size="small"
              placeholder="Ask me about R&D projects happening in a location..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSend()
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              aria-label="Send"
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : <ChatIcon />}
            </IconButton>
          </Box>
        </Box>
      </Drawer>
    </>
  )
}

export default ChatWidget
